import { useEffect, useState } from "react";
import { Progress, Spin } from "antd";
import { useNavigate } from "react-router-dom";

export type RiskLevel = "LOW" | "MODERATE" | "HIGH";

/**
 * Props for RiskProfile component.
 */
export interface RiskProfileProps {
  /** Risk score from 0 to 100. */
  score: number;
  /** Loading state for risk score retrieval. */
  isLoading?: boolean;
  /** Last update date label. */
  lastUpdated: string;
  /** Helper description; if omitted, copy is chosen from score tier. */
  description?: string;
  /** Optional account label shown above the title (multi-account profile). */
  accountLabel?: string;
  /** Target account id for retake; omit/null for initial profile score. */
  accountId?: string | null;
  /** CTA label; defaults based on whether a score already exists. */
  ctaLabel?: string;
}

const MAX_SCORE = 100;

const RISK_TIERS: Record<
  RiskLevel,
  { maxScore: number; color: string; description: string }
> = {
  LOW: {
    maxScore: 33,
    color: "#34C759",
    description:
      "Based on your answers, you prioritize preserving capital and prefer lower volatility, with modest return expectations.",
  },
  MODERATE: {
    maxScore: 66,
    color: "#2979FF",
    description:
      "Based on your answers, you can accept some risk for higher long-term returns, but still value stability.",
  },
  HIGH: {
    maxScore: 100,
    color: "#FF453A",
    description:
      "Based on your answers, you're comfortable with higher volatility in pursuit of stronger long-term growth.",
  },
};

function riskLevelFromScore(score: number): RiskLevel {
  const s = Math.max(0, Math.min(score, MAX_SCORE));
  if (s <= RISK_TIERS.LOW.maxScore) return "LOW";
  if (s <= RISK_TIERS.MODERATE.maxScore) return "MODERATE";
  return "HIGH";
}
/**
 * Displays risk profile with a circular score gauge and linear risk bar.
 */
export const RiskProfile: React.FC<RiskProfileProps> = ({
  score,
  isLoading = false,
  lastUpdated: _lastUpdated,
  description: descriptionProp,
  accountLabel,
  accountId = null,
  ctaLabel,
}) => {
  const navigate = useNavigate();
  const clampedScore = Math.max(0, Math.min(score, MAX_SCORE));
  const hasScore = clampedScore > 0;
  const level = riskLevelFromScore(clampedScore);
  const accentColor = hasScore ? RISK_TIERS[level].color : "#8E8E93";
  const description =
    descriptionProp ??
    (hasScore
      ? RISK_TIERS[level].description
      : "Complete the risk questionnaire to set your risk profile.");
  const buttonLabel =
    ctaLabel ?? (hasScore ? "Retake risk questionnaire" : "Take risk questionnaire");
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    setAnimatedScore(0);
    const frame = requestAnimationFrame(() => setAnimatedScore(clampedScore));
    return () => cancelAnimationFrame(frame);
  }, [clampedScore]);

  return (
    <section
      className="wealth-profile-card risk-profile-card bg-[#1c1c1e] text-[#FFFFFF] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(0,0,0,0.35)]"
      aria-label={accountLabel ? `Risk profile for ${accountLabel}` : "Risk profile"}
    >
      {isLoading ? (
        <div className="flex h-full min-h-[260px] w-full items-center justify-center">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div
            className="w-full"
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "24px",
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: 0,
                maxWidth: "50%",
                textAlign: "left",
              }}
            >
              {accountLabel ? (
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-[#8E8E93]">
                  {accountLabel}
                </p>
              ) : null}
              <h2
                className="section-title"
                style={{ paddingBottom: 0, marginBottom: 6 }}
              >
                Risk Profile
              </h2>
              <h2
                className="font-bold tracking-tight"
                style={{ color: accentColor, margin: 0 }}
              >
                {hasScore ? level : "NOT SET"}
              </h2>
              <p className="mb-4 text-sm leading-5 text-[#8E8E93]">
                {description}
              </p>
              <button
                type="button"
                className="rounded-full border border-white/60 bg-[#1c1c1e] px-5 py-2 text-sm font-medium text-white transition duration-200 hover:brightness-110"
                onClick={() =>
                  navigate("/wealth/open-brokerage-account/step1", {
                    state: {
                      retakeRiskOnly: true,
                      returnPath: "/home/wealth-profile",
                      accountId: accountId ?? null,
                    },
                  })
                }
              >
                {buttonLabel}
              </button>
            </div>

            <div style={{ flexShrink: 0, alignSelf: "center" }}>
              <div className="relative h-[120px] w-[120px]">
                <Progress
                  type="circle"
                  percent={animatedScore}
                  size={160}
                  showInfo
                  format={() => (
                    <span className="flex flex-col items-center justify-center leading-none">
                      <span className="text-[20px] font-semibold text-[#FFFFFF]">
                        {hasScore ? `${clampedScore}/100` : "—"}
                      </span>
                    </span>
                  )}
                  strokeColor={accentColor}
                  trailColor="#3A3A3C"
                  strokeLinecap="round"
                  strokeWidth={10}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 w-full">
            <div>
              <Progress
                percent={animatedScore}
                showInfo={false}
                strokeColor={accentColor}
                trailColor="#3A3A3C"
                strokeLinecap="round"
                strokeWidth={8}
              />
              <div
                style={{
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    lineHeight: "14px",
                    color: "#8E8E93",
                  }}
                >
                  Lower risk
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    lineHeight: "14px",
                    color: "#8E8E93",
                    textAlign: "right",
                  }}
                >
                  Higher risk
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
